/**
 * "Alert on condition" — the metric picker (`GET .../alert-metrics`) and
 * the `story_alerts` create call (`POST .../alerts`). Kept separate from
 * `deliveryRulesApi.ts`: these are workspace/story-scoped routes (like
 * `story_canvas`), not the tenant-scoped `/notifications/rules` routes —
 * see the backend's `alert_metrics_routes.py`/`story_alerts_routes.py`
 * docstrings for why.
 */
import { API_ENDPOINTS, apiService } from "../api";

export interface AlertMetricOptionDto {
  metric_ref: string;
  canvas_item_id: string;
  metric_label: string;
  current_value: number | null;
}

export async function getAlertMetrics(
  workspaceId: string,
  storyId: string,
): Promise<AlertMetricOptionDto[]> {
  return apiService.get<AlertMetricOptionDto[]>(
    API_ENDPOINTS.notifications.alertMetrics(workspaceId, storyId),
  );
}

export type AlertConditionModeDto = "threshold" | "auto";
export type AlertComparatorDto = "exceeds" | "falls_below" | "equals";
export type AlertMatchModeDto = "and" | "or";

export interface AlertConditionDto {
  metric_ref: string;
  canvas_item_id?: string;
  metric_label: string;
  mode: AlertConditionModeDto;
  comparator?: AlertComparatorDto;
  threshold_value?: number;
  /** The metric's live value at the moment this condition was configured
   * — backs the {{old_value}} message token. Never updated after create;
   * the evaluator always re-reads the *current* value separately. */
  baseline_value?: number;
}

export interface AlertConditionGroupDto {
  match: AlertMatchModeDto;
  conditions: AlertConditionDto[];
}

export interface CreateStoryAlertParams {
  workspaceId: string;
  storyId: string;
  conditions: AlertConditionGroupDto;
  messageTemplate: string;
  /** Defaults true server-side when omitted — pass explicitly once the FE
   * exposes a toggle for it (include_story_link still has no FE control
   * yet, so it stays omitted/default). */
  includeAttachment?: boolean;
}

export interface StoryAlertResponseDto {
  id: string;
  story_id: string;
  is_active: boolean;
  conditions: AlertConditionGroupDto;
  include_story_link: boolean;
  include_attachment: boolean;
  attachment_format: "pdf" | "pptx" | null;
  message_template: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export async function createStoryAlert(
  params: CreateStoryAlertParams,
): Promise<StoryAlertResponseDto> {
  return apiService.post<StoryAlertResponseDto>(
    API_ENDPOINTS.notifications.storyAlerts(params.workspaceId, params.storyId),
    {
      conditions: params.conditions,
      message_template: params.messageTemplate,
      ...(params.includeAttachment !== undefined
        ? { include_attachment: params.includeAttachment }
        : {}),
      // include_story_link deliberately omitted — defaults true
      // server-side (see StoryAlertBase); no FE toggle exists for it yet
      // (separate, not-yet-built UI decision).
    },
  );
}
